import { config } from "@app-contexts/sdk/services/config";
import {
  Widget,
  type WidgetLaunchOptions,
  WidgetQueryParams,
} from "@app-contexts/sdk/services/Widget";
import { CUSTOMER_ATTRIBUTION_QUERY_PARAM } from "@shared/modules/basket/session/customer-attribution-messaging";

// handlers are dependent on each other - checkout listener needs calendar
// and calendar needs checkout
// eslint-disable-next-line import/no-cycle
import { calendarWidget } from "./calendar";
import {
  getCustomerAttributionWidgetOptions,
  getEmbeddedWidgetCallArgs,
  getEmbeddedWidgetOptions,
} from "./embedded-widget-options";
import { createEmbeddedWidgetTransition } from "./embedded-widget-transition";
import {
  inheritCustomerAttributionToken,
  omitCustomerAttributionToken,
  stripCustomerAttributionTokenFromUrl,
} from "./customer-attribution";

export const SDK_PARAMS_STORAGE_KEY = "showpass-sdk-widget-params";

export const PAYMENT_REDIRECT_PARAMS = [
  "affirm",
  "alipay",
  "stripe",
  "payment_intent",
  "payment_intent_client_secret",
  "setup_intent",
  "setup_intent_client_secret",
  "redirect_status",
] as const;

const PARENT_PAGE_UTM_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

export function getCleanPageUrl(): string {
  const url = new URL(window.location.href);
  PAYMENT_REDIRECT_PARAMS.forEach((param) => url.searchParams.delete(param));
  url.searchParams.delete(CUSTOMER_ATTRIBUTION_QUERY_PARAM);
  return url.toString();
}

function withRedirectUrl(params: WidgetQueryParams): WidgetQueryParams {
  if (params["redirect-url"]) {
    return {
      ...params,
      "redirect-url": stripCustomerAttributionTokenFromUrl(
        params["redirect-url"]
      ),
    };
  }

  return {
    ...params,
    "redirect-url": encodeURIComponent(getCleanPageUrl()),
  };
}

function withParentPageUtmParams(params: WidgetQueryParams): WidgetQueryParams {
  const url = new URL(window.location.href);
  const parentPageUtmParams: WidgetQueryParams = {};

  PARENT_PAGE_UTM_PARAMS.forEach((param) => {
    if (Object.prototype.hasOwnProperty.call(params, param)) return;

    const value = url.searchParams.get(param);
    if (value) {
      parentPageUtmParams[param] = value;
    }
  });

  return {
    ...parentPageUtmParams,
    ...params,
  };
}

function saveWidgetParams(params: WidgetQueryParams): void {
  try {
    sessionStorage.setItem(
      SDK_PARAMS_STORAGE_KEY,
      JSON.stringify(omitCustomerAttributionToken(params))
    );
  } catch {
    // sessionStorage may be unavailable (e.g. some privacy modes)
  }
}

export function prepareWidgetParams(
  params: WidgetQueryParams
): WidgetQueryParams {
  const enrichedParams = withRedirectUrl(withParentPageUtmParams(params));
  saveWidgetParams(enrichedParams);
  return enrichedParams;
}

/**
 * Widget to show the checkout flow only
 * @param params values to add to URL params (e.g. 'theme-primary', 'theme-secondary', etc.)
 * @param containerId id of the container to embed the widget in
 * @param options optional host callback used to refresh a rejected attribution token
 */
export async function checkoutWidget(
  params: WidgetQueryParams,
  containerId?: string,
  options: WidgetLaunchOptions = {}
) {
  const enrichedParams = prepareWidgetParams(params);

  const widget = new Widget({
    src: await config.checkoutWidgetUrl(),
    queryParams: enrichedParams,
    containerId,
    ...getEmbeddedWidgetOptions(containerId),
    ...getCustomerAttributionWidgetOptions(enrichedParams, options),
  });
  const replaceEmbeddedWidget = createEmbeddedWidgetTransition(widget);

  widget
    .on("showpass.checkout.close", () => {
      if (containerId) {
        window.parent.postMessage("showpass.calendar.remount", "*");
      }
    })
    .show()
    .on("showpass.checkout.back-to-event", ({ slug, params }) => {
      const eventParams = inheritCustomerAttributionToken(
        enrichedParams,
        params
      );

      if (containerId) {
        replaceEmbeddedWidget(() =>
          eventPurchaseWidget(
            slug,
            eventParams,
            ...getEmbeddedWidgetCallArgs(containerId, options)
          )
        );
      } else {
        eventPurchaseWidget(
          slug,
          eventParams,
          ...getEmbeddedWidgetCallArgs(undefined, options)
        );
      }
    });

  return widget;
}

/**
 * Shows the express checkout widget
 * @param params values to add to URL params (e.g. 'theme-primary', 'theme-secondary', etc.)
 * @param containerId id of the container to embed the widget in
 * @param options optional host callback used to refresh a rejected attribution token
 */
export async function expressCheckoutWidget(
  params: WidgetQueryParams,
  containerId?: string,
  options: WidgetLaunchOptions = {}
) {
  const enrichedParams = prepareWidgetParams(params);

  const widget = new Widget({
    src: await config.expressCheckoutWidgetUrl(),
    queryParams: enrichedParams,
    containerId,
    ...getEmbeddedWidgetOptions(containerId),
    ...getCustomerAttributionWidgetOptions(enrichedParams, options),
  });

  widget.show();

  return widget;
}

/**
 * Shows an event purchase widget for the specified event
 * @param slug slug of event
 * @param params values to add to URL params (e.g. 'theme-primary', 'theme-secondary', etc.)
 * @param containerId id of the container to embed the widget in
 * @param options optional host callback used to refresh a rejected attribution token
 */
export async function eventPurchaseWidget(
  slug = "",
  params: WidgetQueryParams = {},
  containerId?: string,
  options: WidgetLaunchOptions = {}
) {
  const enrichedParams = prepareWidgetParams(params);
  const customerAttributionToken =
    enrichedParams[CUSTOMER_ATTRIBUTION_QUERY_PARAM];
  const widgetParams = {
    ...enrichedParams,
    "theme-primary": enrichedParams["theme-primary"],
    "theme-secondary":
      enrichedParams["theme-secondary"] || enrichedParams["theme-primary"],
  };

  const widget = new Widget({
    src: await config.eventPurchaseWidgetUrl(slug),
    containerId,
    ...getEmbeddedWidgetOptions(containerId),
    ...getCustomerAttributionWidgetOptions(widgetParams, options, [
      enrichedParams,
    ]),
    ...(typeof customerAttributionToken === "string" &&
      customerAttributionToken.trim() && {
        onCustomerAttributionReleased: () => {
          delete widgetParams[CUSTOMER_ATTRIBUTION_QUERY_PARAM];
          delete enrichedParams[CUSTOMER_ATTRIBUTION_QUERY_PARAM];
        },
      }),
    queryParams: widgetParams,
  });
  const replaceEmbeddedWidget = createEmbeddedWidgetTransition(widget);

  const transitionToCheckout = () => {
    if (containerId) {
      replaceEmbeddedWidget(() =>
        checkoutWidget(
          enrichedParams,
          ...getEmbeddedWidgetCallArgs(containerId, options)
        )
      );
    } else {
      widget.close({ removeBodyClass: false });
      checkoutWidget(
        enrichedParams,
        ...getEmbeddedWidgetCallArgs(undefined, options)
      );
    }
  };

  widget
    .show()
    .on("showpass.event-purchase.close", () => {
      if (containerId) {
        window.parent.postMessage("showpass.calendar.remount", "*");
      }
      widget.close();
    })
    .on("showpass.event-purchase.back-to-calendar", ({ venueId, params }) => {
      const calendarParams = inheritCustomerAttributionToken(
        enrichedParams,
        params
      );

      if (containerId) {
        replaceEmbeddedWidget(() =>
          calendarWidget(
            venueId,
            calendarParams,
            ...getEmbeddedWidgetCallArgs(containerId, options)
          )
        );
      } else {
        calendarWidget(
          venueId,
          calendarParams,
          ...getEmbeddedWidgetCallArgs(undefined, options)
        );
      }
    })
    .on("showpass.event-purchase.checkout", transitionToCheckout)
    .on("showpass.widget.next-click", transitionToCheckout)
    .on("showpass.event-purchase.express-checkout", () => {
      if (containerId) {
        replaceEmbeddedWidget(() =>
          expressCheckoutWidget(
            enrichedParams,
            ...getEmbeddedWidgetCallArgs(containerId, options)
          )
        );
      } else {
        widget.close({ removeBodyClass: false });
        expressCheckoutWidget(
          enrichedParams,
          ...getEmbeddedWidgetCallArgs(undefined, options)
        );
      }
    });

  return widget;
}
